import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Store, ArrowLeft } from 'lucide-react';

const Section = ({ title, children }) => (
  <div className="mb-8">
    <h2 className="text-lg font-semibold text-gray-900 mb-3">{title}</h2>
    <div className="text-gray-600 space-y-2 leading-relaxed">{children}</div>
  </div>
);

export default function TermsOfService() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-2">
            <div className="w-7 h-7 bg-emerald-600 rounded-lg flex items-center justify-center">
              <Store className="h-4 w-4 text-white" />
            </div>
            <span className="font-bold text-gray-900">KiranaFlow</span>
          </Link>
          <Link to="/home" className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors">
            <ArrowLeft className="h-4 w-4" /> Back
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 md:p-12">
          <h1 className="text-3xl font-extrabold text-gray-900 mb-2">Terms of Service</h1>
          <p className="text-sm text-gray-400 mb-10">Effective date: 15 May 2026 · Last updated: 15 May 2026</p>

          <p className="text-gray-600 mb-8 leading-relaxed">
            These Terms of Service ("Terms") govern your access to and use of KiranaFlow, a cloud-based ERP
            and point-of-sale software ("Service") operated by KiranaFlow ("we", "us", "our"). By accessing
            or using the Service, you agree to be bound by these Terms. If you do not agree, you must not use the Service.
          </p>

          <Section title="1. Eligibility">
            <p>You must be at least 18 years of age and have the legal authority to enter into contracts under applicable Indian law to use this Service. By agreeing to these Terms you represent that you meet this requirement.</p>
          </Section>

          <Section title="2. Account Registration">
            <p>Accounts are created by KiranaFlow on your behalf following direct onboarding. You are responsible for maintaining the confidentiality of your login credentials and for all activities that occur under your account. Notify us immediately at <a href="mailto:rohankakad712@gmail.com" className="text-emerald-600 underline">rohankakad712@gmail.com</a> if you suspect unauthorised access.</p>
          </Section>

          <Section title="3. Subscription Plans and Fees">
            <p>KiranaFlow offers three subscription tiers — Starter, Growth, and Enterprise — each with different feature sets described on our pricing page.</p>
            <p>Subscription fees are billed in Indian Rupees (INR). Payment is currently collected manually (bank transfer, UPI, or other agreed method) prior to activation or renewal of your subscription. We will issue a confirmation upon receipt of payment.</p>
            <p>We reserve the right to update pricing with 30 days' advance written notice. Continued use after the notice period constitutes acceptance of the new pricing.</p>
          </Section>

          <Section title="4. Refunds and Cancellations">
            <p>Please refer to our <Link to="/refund" className="text-emerald-600 underline">Refund Policy</Link> for full details. In summary: subscription fees are non-refundable except where we are unable to provide the Service or where required by applicable law.</p>
          </Section>

          <Section title="5. Permitted Use">
            <p>You may use the Service solely for your own internal retail or wholesale business operations. You must not:</p>
            <ul className="list-disc ml-5 space-y-1 mt-1">
              <li>Resell, sublicense, or make the Service available to third parties as a standalone product</li>
              <li>Reverse-engineer, decompile, or attempt to extract the source code of the Service</li>
              <li>Use the Service to store or transmit unlawful, harmful, or fraudulent data</li>
              <li>Interfere with or disrupt the integrity or performance of the Service or its underlying infrastructure</li>
              <li>Attempt to gain unauthorised access to any other customer's data or account</li>
            </ul>
          </Section>

          <Section title="6. Your Data">
            <p>You retain full ownership of all data you upload to or generate within the Service ("Customer Data"). We process Customer Data solely to provide and improve the Service in accordance with our <Link to="/privacy" className="text-emerald-600 underline">Privacy Policy</Link>.</p>
            <p>You may export your Customer Data at any time using the built-in CSV export features. We will provide reasonable assistance for data export requests submitted to <a href="mailto:rohankakad712@gmail.com" className="text-emerald-600 underline">rohankakad712@gmail.com</a>.</p>
            <p>Upon termination of your account we will retain your data for 30 days before permanent deletion, unless a longer retention period is required by law.</p>
          </Section>

          <Section title="7. Service Availability and Maintenance">
            <p>We aim to maintain high availability of the Service but do not guarantee uninterrupted access. Scheduled maintenance will be communicated in advance where possible. We are not liable for any losses arising from downtime, outages, or data unavailability beyond our reasonable control.</p>
          </Section>

          <Section title="8. Intellectual Property">
            <p>The Service, including all software, design, and content created by us, is owned by KiranaFlow and protected under applicable intellectual property laws. These Terms grant you a limited, non-exclusive, non-transferable licence to use the Service during your subscription term.</p>
          </Section>

          <Section title="9. Limitation of Liability">
            <p>To the maximum extent permitted by law, KiranaFlow's total liability for any claim arising out of or relating to these Terms or the Service shall not exceed the fees paid by you in the three months preceding the claim. We are not liable for indirect, incidental, special, or consequential damages, including loss of profits, loss of data, or business interruption.</p>
          </Section>

          <Section title="10. Disclaimers">
            <p>The Service is provided "as is" without warranties of any kind, express or implied. We do not warrant that the Service will be error-free, that GST calculations are complete or suitable for filing without review, or that AI-generated outputs (invoice parsing, smart actions) are accurate in all cases. You remain responsible for verifying outputs before acting on them.</p>
          </Section>

          <Section title="11. Governing Law and Disputes">
            <p>These Terms are governed by and construed in accordance with the laws of India. Any dispute arising out of or relating to these Terms shall first be attempted to be resolved by good-faith negotiation. If unresolved within 30 days, disputes shall be subject to the exclusive jurisdiction of the courts of Pune, Maharashtra.</p>
          </Section>

          <Section title="12. Changes to These Terms">
            <p>We may update these Terms from time to time. We will notify you of material changes by email or in-app notice at least 14 days before they take effect. Continued use after that date constitutes acceptance of the revised Terms.</p>
          </Section>

          <Section title="13. Contact Us">
            <p>For any questions about these Terms, contact us at:</p>
            <p className="mt-2">
              <strong>KiranaFlow</strong><br />
              Email: <a href="mailto:rohankakad712@gmail.com" className="text-emerald-600 underline">rohankakad712@gmail.com</a><br />
              Phone: +91 88888 55212
            </p>
          </Section>

          <div className="mt-10 pt-6 border-t border-gray-100 flex flex-wrap gap-4 text-sm text-gray-400">
            <Link to="/privacy" className="hover:text-emerald-600 transition-colors">Privacy Policy</Link>
            <Link to="/refund"  className="hover:text-emerald-600 transition-colors">Refund Policy</Link>
            <Link to="/home"    className="hover:text-emerald-600 transition-colors">Back to Home</Link>
          </div>
        </div>
      </main>
    </div>
  );
}
